'use client';

import { DealerStore } from '../../dealerStoreType';
import { useExpander } from '../../../../hooks/useExpander';
import expanderStyles from '../../../common/expander/styles.module.scss';
import { CatRadioField } from '@blocks-react/components/RadioField';
import { DealerLocatorSearchResultAddress } from '../dealer-locator-search-result-address-information';
import { DealerLocatorSearchResultBranchName } from '../dealer-locator-search-result-branch-name';
import { DealerLocatorSearchResultDistance } from '../dealer-locator-search-result-distance';
import { DealerLocatorSearchResultName } from '../dealer-locator-search-result-name';
import { DealerLocatorNonInstantAccessWarning } from '../dealer-locator-non-instant-access-warning';
import { useFormContext } from 'react-hook-form';
import { useRadioController } from '../../../../hooks/form-controllers/useRadioFieldController';
import { Conditional } from '../../../common/conditional';
import { Expander } from '../../../common/expander';
import { SELECTED_STORE_ID } from '../../constants';
import { useEffect, useRef } from 'react';
import { DealerLocatorSearchResultMobileContact } from '../dealer-locator-search-result-mobile-contact/DealerLocatorSearchResultMobileContact';
interface DealerLocatorSearchResultProps {
  children: React.ReactNode;
  store: DealerStore;
  unitOfMeasure?: 'mi' | 'km';
  closestLabel: string;
  setScrollToStore?: (arg1: boolean) => void;
  isDisabled?: boolean;
  showNonInstantAccessWarning?: boolean;
  warningMessage?: string;
  showAvailabilityBTNAndMsg?: boolean;
  availabilityBtnAndMsg?: React.ReactNode;
  isStoreLocationListViewUpdateFlag?: boolean;
}

export const DealerLocatorSearchResult = ({
  children,
  store,
  unitOfMeasure,
  closestLabel,
  setScrollToStore,
  isDisabled,
  showNonInstantAccessWarning = false,
  warningMessage = '',
  showAvailabilityBTNAndMsg = false,
  availabilityBtnAndMsg,
  isStoreLocationListViewUpdateFlag = false
}: DealerLocatorSearchResultProps) => {
  const { isExpanded, toggle } = useExpander();
  const onExpandHandler = () => toggle(!isDisabled && !isExpanded);
  const { setValue, watch } = useFormContext();
  const { getRadioField } = useRadioController({ name: SELECTED_STORE_ID });
  const selectedStoreRef = useRef<HTMLDivElement | null>(null);
  const selectedStoreId: string = watch(SELECTED_STORE_ID);

  const unitOfMeasureLabel = `${unitOfMeasure ? ' ' : ''}${
    unitOfMeasure || ''
  }`;
  const distanceLabel = `${Number(store.geo.distance).toFixed(
    1
  )}${unitOfMeasureLabel}`;
  const address = `${store.address.line1 || ''}${
    store.address.line1 ? ',' : ''
  } ${store.address.state || ''} ${store.address.zipCode || ''}`;
  const isSelected = store.id.toString() === selectedStoreId;

  useEffect(() => {
    if (!isSelected) {
      selectedStoreRef.current = null;
    }
  }, [isSelected]);

  //scrolls to selected store when the user returns from map view and setScrollToStore is defined
  useEffect(() => {
    if (setScrollToStore && selectedStoreRef.current !== null) {
      setTimeout(() => {
        selectedStoreRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
        setScrollToStore(false);
      }, 100);
    }
    toggle(false);
  }, [setScrollToStore, toggle]);

  const handleSelect = () => {
    setValue(SELECTED_STORE_ID, store.id.toString());
  };

  useEffect(() => {
    if (isExpanded) {
      onExpandHandler();
    }
  }, [store]);

  const distanceIsValid =
    (store.geo.distance || store.geo.distance === 0) &&
    !isNaN(store.geo.distance);

  const dealerLocatorSearchResultCard = () => {
    return (
      <>
        <div className="d-flex justify-content-between">
          <div>
            <CatRadioField
              {...getRadioField(SELECTED_STORE_ID, store.id.toString())}
              onBlRadioChange={handleSelect}
              className="mt-0.5"
              disabled={isDisabled}
            >
              <DealerLocatorSearchResultName
                isNonInstantAccessDealer={showNonInstantAccessWarning}
              >
                {store.dealerName}
              </DealerLocatorSearchResultName>
            </CatRadioField>
            <DealerLocatorSearchResultBranchName>
              {store.name}
            </DealerLocatorSearchResultBranchName>
            <DealerLocatorSearchResultAddress>
              {address}
            </DealerLocatorSearchResultAddress>

            <Conditional test={showNonInstantAccessWarning}>
              <DealerLocatorNonInstantAccessWarning>
                {warningMessage}
              </DealerLocatorNonInstantAccessWarning>
            </Conditional>
          </div>
          <DealerLocatorSearchResultDistance>
            {store.geo.isClosest && <p className="mb-0">{closestLabel}</p>}
            {distanceIsValid ? (
              <p className="text-nowrap">{distanceLabel}</p>
            ) : null}
          </DealerLocatorSearchResultDistance>
        </div>
        <Expander isExpanded={isExpanded} onExpandHandler={onExpandHandler} />
        <Conditional test={!!showAvailabilityBTNAndMsg}>
          {availabilityBtnAndMsg}
        </Conditional>
      </>
    );
  };
  return (
    <div
      className={expanderStyles['expander-container']}
      data-testid="search-result-component"
      ref={isSelected ? selectedStoreRef : null}
    >
      <Conditional test={!isStoreLocationListViewUpdateFlag}>
        <div className={`${expanderStyles['expander-header']} py-3 px-1`}>
          {dealerLocatorSearchResultCard()}
        </div>
      </Conditional>
      <Conditional test={isStoreLocationListViewUpdateFlag}>
        <div
          className={`${expanderStyles['expander-header']} py-3 px-1 d-none d-md-block`}
        >
          {dealerLocatorSearchResultCard()}
        </div>
      </Conditional>
      <Conditional test={isStoreLocationListViewUpdateFlag}>
        <div className={` py-3 px-1 d-block d-md-none`}>
          <div className="d-flex align-items-start align-self-stretch">
            <CatRadioField
              {...getRadioField(SELECTED_STORE_ID, store.id.toString())}
              onBlRadioChange={handleSelect}
              className="mt-0.5"
              disabled={isDisabled}
            ></CatRadioField>
            <div className="d-flex align-items-start">
              <div className="d-flex flex-column align-items-start">
                <DealerLocatorSearchResultName
                  isNonInstantAccessDealer={showNonInstantAccessWarning}
                  isStoreLocationListViewUpdateFlag={
                    isStoreLocationListViewUpdateFlag
                  }
                >
                  {store.dealerName}
                </DealerLocatorSearchResultName>
                <DealerLocatorSearchResultBranchName>
                  {store.name}
                </DealerLocatorSearchResultBranchName>
                <DealerLocatorSearchResultAddress>
                  {address}
                </DealerLocatorSearchResultAddress>
                <DealerLocatorSearchResultMobileContact>
                  {store.address.phone}
                </DealerLocatorSearchResultMobileContact>
                <Conditional test={showNonInstantAccessWarning}>
                  <DealerLocatorNonInstantAccessWarning>
                    {warningMessage}
                  </DealerLocatorNonInstantAccessWarning>
                </Conditional>
              </div>
              <div
                className="d-flex flex-column align-items-end 
              justify-content-between
              "
              >
                <DealerLocatorSearchResultDistance>
                  {store.geo.isClosest && (
                    <p className="mb-0">{closestLabel}</p>
                  )}
                  {distanceIsValid ? (
                    <p className="text-nowrap">{distanceLabel}</p>
                  ) : null}
                </DealerLocatorSearchResultDistance>
              </div>
            </div>
          </div>
          <Conditional test={!!showAvailabilityBTNAndMsg}>
            {availabilityBtnAndMsg}
          </Conditional>
        </div>
      </Conditional>

      <Conditional test={isExpanded}>{children}</Conditional>
    </div>
  );
};
